import { useState, useEffect, useRef } from 'react';
import type React from 'react';
import { Grid, Button, Box, Flex, FlexItem } from '@gusto/workbench';
import { postJson } from 'requests/http';
import { Form, SubmitButton, TextField, NumberField } from '@gusto/workbench-formik';
import PasswordStrengthBar from 'components/password_form/password_strength_bar';
import PasswordValidator from 'components/password_form/password_validator';
import Terms from 'packages/signup/components/signupForm/Terms';
import type { FormDetailsValues } from 'packages/signup/components/signupForm/types';
import AccountAlreadyExistsDialog from './accountAlreadyExists/accountAlreadyExistsDialog';
import { uniqueEmailSchema } from './validation_schema';
import FormattedPhoneField from './formattedPhoneField';

type FormDetailsProps = {
  values: FormDetailsValues;
  setStatus: (value: object) => void;
  fromSSO: boolean;
  ssoProvider?: string;
  emailStatus?: React.ReactElement;
  errorStatus: number;
  primaryCTAOnClick?: () => void;
};

export default function FormDetails({
  values,
  setStatus,
  fromSSO,
  ssoProvider,
  emailStatus,
  errorStatus,
  primaryCTAOnClick,
}: FormDetailsProps) {
  const [type, setType] = useState('password');
  const [isAccountAlreadyExistsDialogOpen, setIsAccountAlreadyExistsDialogOpen] = useState(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const autoValidate = setTimeout(() => {
      uniqueEmailSchema(postJson)
        .validate(values.email)
        .then(() => setStatus({ email: null }))
        .catch(({ message }) => {
          setStatus({ email: message });
          setIsAccountAlreadyExistsDialogOpen(true);
        });
    }, 300);

    if (ssoProvider === 'intuit' && submitButtonRef.current) {
      submitButtonRef.current.click();
    }

    return () => clearTimeout(autoValidate);
  }, [values.email, setStatus, ssoProvider]);

  useEffect(() => {
    if (errorStatus === 409) {
      setIsAccountAlreadyExistsDialogOpen(true);
    }
  }, [errorStatus]);

  const updateEmailField = () => {
    // eslint-disable-next-line no-param-reassign
    values.email = '';
  };

  return (
    <>
      <Grid
        component={Form}
        maxWidth={632}
        width="100%"
        autoRows="max-content"
        rowGap={3}
        data-testid="company-signup-form-test"
      >
        <Flex columnGap={3} rowGap={3} flexDirection="column" flexDirectionMd="row">
          <FlexItem width={1} widthMd={1 / 2}>
            <TextField
              name="firstName"
              type="text"
              label="First name"
              required
              autoComplete="given-name"
            />
          </FlexItem>
          <FlexItem width={1} widthMd={1 / 2}>
            <TextField
              name="lastName"
              type="text"
              label="Last name"
              required
              autoComplete="family-name"
            />
          </FlexItem>
        </Flex>
        <TextField
          name="email"
          type="email"
          label="Work email"
          required
          invalid={!!emailStatus}
          validationText={emailStatus}
          autoComplete="email"
          readOnly={!!fromSSO}
        />
        <TextField name="companyName" type="text" label="Company name" required />
        <Flex columnGap={3} rowGap={3} flexDirection="column" flexDirectionMd="row">
          <FlexItem width={1} widthMd={1 / 2}>
            <NumberField
              name="numberEmployees"
              label="No. of employees"
              inputMode="numeric"
              required
              min={0}
              max={99999}
              step={1}
            />
          </FlexItem>
          <FlexItem width={1} widthMd={1 / 2}>
            <FormattedPhoneField
              name="phone"
              label="Company phone"
              type="tel"
              placeholder="(555) 012-3456"
              autoComplete="tel"
            />
          </FlexItem>
        </Flex>
        {!fromSSO && (
          <>
            <TextField
              label="Set password"
              type={type}
              name="password"
              required
              autoComplete="new-password"
              after={
                <Button
                  variant="link"
                  style={{ pointerEvents: 'auto' }}
                  onClick={() => setType(type === 'password' ? 'text' : 'password')}
                  tabIndex={-1}
                >
                  {type === 'password' ? 'show' : 'hide'}
                </Button>
              }
            />
            <Box>
              <PasswordStrengthBar password={values.password} />
              <div className="padding-left-15px text-left ml-n4">
                <PasswordValidator password={values.password} />
              </div>
            </Box>
            <TextField
              label="Confirm password"
              type={type}
              name="passwordConfirmation"
              required
              autoComplete="new-password"
              after={
                <Button
                  variant="link"
                  style={{ pointerEvents: 'auto' }}
                  onClick={() => setType(type === 'password' ? 'text' : 'password')}
                  tabIndex={-1}
                >
                  {type === 'password' ? 'show' : 'hide'}
                </Button>
              }
            />
          </>
        )}
        <div className="margin-top-16px margin-bottom-4px">
          <Terms />
        </div>
        <div className="btn-rainbow-plate margin-top-15px margin-bottom-15px">
          <SubmitButton
            className="btn btn-submit-form btn--rainbow width-100"
            disabled={!!emailStatus}
            onClick={primaryCTAOnClick}
            ref={submitButtonRef}
          >
            Create account
          </SubmitButton>
        </div>
      </Grid>
      <AccountAlreadyExistsDialog
        isOpen={isAccountAlreadyExistsDialogOpen}
        setIsOpen={setIsAccountAlreadyExistsDialogOpen}
        updateEmailField={() => updateEmailField()}
      />
    </>
  );
}
